import React from 'react';
import { ChevronDownIcon, SearchIcon, XIcon } from 'lucide-react';
import { twMerge } from 'tailwind-merge';
import { useDebounce } from '@shared/hooks/useDebounce';
import { INPUT_BASE, INPUT_STATE } from '@/shared/components/form/input/inputStyles';

export interface SearchableSelectProps<TOption> {
    options: TOption[];
    value: TOption | null;
    onChange: (next: TOption | null) => void;
    getOptionValue: (o: TOption) => string;
    getOptionLabel: (o: TOption) => string;
    /** Present => async mode: emit the debounced term; parent queries and feeds options+loading back. */
    onSearchChange?: (term: string) => void;
    loading?: boolean;
    placeholder?: string;
    disabled?: boolean;
    /** Show an "x" / Clear affordance that resets the selection to null. */
    clearable?: boolean;
    /** Hide options whose value is in this list (e.g. already-attached). */
    excludeValues?: string[];
    /** Text shown when there are no options to display. */
    emptyLabel?: string;
    /** Optional custom row renderer; defaults to getOptionLabel(o). */
    renderOption?: (o: TOption) => React.ReactNode;
    id?: string;
    'aria-invalid'?: boolean;
    'aria-describedby'?: string;
    /** Show a right-hand chevron button and open the list on input click. */
    showToggle?: boolean;
    /** Called when the open list is scrolled near the bottom while hasMore is true. */
    onLoadMore?: () => void;
    hasMore?: boolean;
    /** Next-page failure message; shows a trailing Retry row and pauses scroll loading. */
    loadMoreError?: string;
}

const LOAD_MORE_THRESHOLD_PX = 48;

export function SearchableSelect<TOption>({
    options,
    value,
    onChange,
    getOptionValue,
    getOptionLabel,
    onSearchChange,
    loading = false,
    placeholder,
    disabled = false,
    clearable = false,
    excludeValues = [],
    emptyLabel = 'No matches.',
    renderOption,
    id,
    'aria-invalid': ariaInvalid,
    'aria-describedby': ariaDescribedBy,
    showToggle = false,
    onLoadMore,
    hasMore = false,
    loadMoreError,
}: SearchableSelectProps<TOption>): React.ReactElement {
    const [search, setSearch] = React.useState('');
    const [open, setOpen] = React.useState(false);
    const [highlighted, setHighlighted] = React.useState(0);
    // Whether the user has started typing since focusing; while false, a
    // selected value's label stays visible in the input (see displayValue).
    const [typing, setTyping] = React.useState(false);
    const debounced = useDebounce(search, 300);
    const inputRef = React.useRef<HTMLInputElement>(null);
    // Blocks repeat requests from a scroll burst until the parent re-renders loading or new results.
    const loadMoreRequested = React.useRef(false);
    const listRef = React.useRef<HTMLUListElement>(null);
    const blurCloseTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
    const rid = React.useId();
    const listboxId = `${rid}-listbox`;
    const optionId = (idx: number) => `${rid}-opt-${idx}`;

    const isAsync = typeof onSearchChange === 'function';

    // Async mode: emit the settled term so the parent can query. Local mode: no-op.
    React.useEffect(() => {
        if (onSearchChange) onSearchChange(debounced);
    }, [debounced, onSearchChange]);

    // Reset the highlight to the top as the query term changes.
    React.useEffect(() => {
        setHighlighted(0);
    }, [search, debounced]);

    const cancelBlurClose = React.useCallback(() => {
        if (blurCloseTimer.current === null) return;
        clearTimeout(blurCloseTimer.current);
        blurCloseTimer.current = null;
    }, []);

    React.useEffect(() => cancelBlurClose, [cancelBlurClose]);

    React.useEffect(() => {
        loadMoreRequested.current = false;
    }, [loading, hasMore, options.length, loadMoreError]);

    const exclude = new Set(excludeValues);
    const base = options.filter((o) => !exclude.has(getOptionValue(o)));
    // Async mode renders the server results verbatim; local mode filters in memory.
    const visible = isAsync
        ? base
        : base.filter((o) => getOptionLabel(o).toLowerCase().includes(search.trim().toLowerCase()));
    const showLoadMoreError = Boolean(onLoadMore && loadMoreError) && !loading;
    const canLoadMore = Boolean(onLoadMore) && hasMore && !loading && !loadMoreError;
    // The Retry row is one extra option after the last result; it requests more and never selects.
    const retryIndex = visible.length;
    const navigableCount = visible.length + (showLoadMoreError ? 1 : 0);
    // Clamp into [0, count-1] so a shrunk or empty list never leaves the highlight out of range.
    const activeIndex = navigableCount ? Math.min(Math.max(highlighted, 0), navigableCount - 1) : 0;
    const retryActive = showLoadMoreError && activeIndex === retryIndex;
    const selectedValue = value !== null ? getOptionValue(value) : null;
    const selectedIndex = visible.findIndex((o) => getOptionValue(o) === selectedValue);

    const openList = () => {
        setHighlighted(selectedIndex >= 0 ? selectedIndex : 0);
        setOpen(true);
    };

    React.useEffect(() => {
        if (!open) return;
        document.getElementById(optionId(activeIndex))?.scrollIntoView?.({ block: 'nearest' });
        // eslint-disable-next-line react-hooks/exhaustive-deps -- optionId is derived from the stable useId value
    }, [open, activeIndex]);

    const select = (o: TOption) => {
        onChange(o);
        setOpen(false);
        setSearch('');
        setTyping(false);
    };

    const clear = () => {
        onChange(null);
        setSearch('');
        setTyping(false);
        inputRef.current?.focus();
    };

    const toggleOpen = () => {
        if (disabled) return;
        if (open) {
            setOpen(false);
            return;
        }
        cancelBlurClose();
        inputRef.current?.focus();
        openList();
    };

    const requestMore = React.useCallback(() => {
        if (!onLoadMore || loadMoreRequested.current) return;
        loadMoreRequested.current = true;
        onLoadMore();
    }, [onLoadMore]);

    // A list too short to scroll can never fire onScroll, so fill it until it overflows.
    // clientHeight 0 means the list is not laid out yet (e.g. jsdom); skip until measured.
    React.useEffect(() => {
        const el = listRef.current;
        if (!open || !canLoadMore || !el || el.clientHeight === 0) return;
        if (el.scrollHeight <= el.clientHeight) requestMore();
    }, [open, canLoadMore, visible.length, requestMore]);

    const handleListScroll = (e: React.UIEvent<HTMLUListElement>) => {
        if (!canLoadMore) return;
        const el = e.currentTarget;
        if (el.scrollHeight - el.scrollTop - el.clientHeight < LOAD_MORE_THRESHOLD_PX) requestMore();
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (!open) {
            if (e.key === 'ArrowDown' || e.key === 'Enter') {
                if (e.key === 'Enter') e.preventDefault();
                openList();
            }
            return;
        }
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            if (canLoadMore && activeIndex === visible.length - 1) requestMore();
            // Math.max(navigableCount - 1, 0) keeps the index at 0 (never -1) when the list is empty.
            setHighlighted(Math.min(activeIndex + 1, Math.max(navigableCount - 1, 0)));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setHighlighted(Math.max(activeIndex - 1, 0));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (retryActive) requestMore();
            else if (visible[activeIndex]) select(visible[activeIndex]);
        } else if (e.key === 'Escape') {
            // Stops a surrounding dialog or filter panel from also closing on this Escape.
            e.preventDefault();
            e.stopPropagation();
            setOpen(false);
        }
    };

    const showClear = clearable && value !== null && !disabled;
    const loadingMore = Boolean(onLoadMore) && loading && visible.length > 0;
    // An empty page with more to come is not "no matches"; show Loading until the next page settles.
    const showLeadingLoading = !loadingMore && ((isAsync && loading) || (visible.length === 0 && canLoadMore));
    const showEmpty = visible.length === 0 && !showLeadingLoading && !showLoadMoreError;
    const leftPadding = showToggle ? 'pl-3' : 'pl-10';
    const rightPadding = showToggle ? (showClear ? 'pr-16' : 'pr-10') : showClear ? 'pr-10' : 'pr-3';
    // Keep the selected label visible while focused-but-not-yet-typing; the
    // input content is selected on focus so the first keystroke replaces it.
    const displayValue = open && typing ? search : value !== null ? getOptionLabel(value) : '';

    return (
        <div className="relative">
            {!showToggle && (
                <span
                    className={`absolute -translate-y-1/2 pointer-events-none left-3 top-1/2 text-mp-muted ${
                        disabled ? ' opacity-40' : ''
                    }`}
                >
                    <SearchIcon className="size-5" />
                </span>
            )}
            <input
                ref={inputRef}
                id={id}
                type="text"
                role="combobox"
                aria-expanded={open}
                aria-autocomplete="list"
                aria-controls={open ? listboxId : undefined}
                aria-activedescendant={open && navigableCount ? optionId(activeIndex) : undefined}
                aria-invalid={ariaInvalid}
                aria-describedby={ariaDescribedBy}
                className={twMerge(
                    INPUT_BASE,
                    INPUT_STATE.default,
                    leftPadding,
                    rightPadding,
                    ariaInvalid && INPUT_STATE.error,
                    disabled && INPUT_STATE.disabled
                )}
                placeholder={open || value === null ? placeholder : undefined}
                value={displayValue}
                disabled={disabled}
                onChange={(e) => {
                    setTyping(true);
                    setSearch(e.target.value);
                    setOpen(true);
                }}
                onFocus={() => {
                    if (disabled) return;
                    cancelBlurClose();
                    if (!open) openList();
                    // Select the visible label so typing replaces the current selection.
                    if (value !== null) {
                        requestAnimationFrame(() => inputRef.current?.select());
                    }
                }}
                onClick={
                    showToggle
                        ? () => {
                              if (disabled) return;
                              cancelBlurClose();
                              if (!open) openList();
                          }
                        : undefined
                }
                onKeyDown={handleKeyDown}
                onBlur={() => {
                    cancelBlurClose();
                    blurCloseTimer.current = setTimeout(() => {
                        blurCloseTimer.current = null;
                        setOpen(false);
                        setTyping(false);
                    }, 150);
                }}
            />
            {showClear && (
                <button
                    type="button"
                    aria-label="Clear selection"
                    className={`absolute ${showToggle ? 'right-10' : 'right-3'} top-1/2 -translate-y-1/2 text-mp-muted hover:text-mp-foreground`}
                    // preventDefault on pointer keeps input focus; onClick handles
                    // both mouse and keyboard (Enter/Space) activation.
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={clear}
                >
                    <XIcon size={18} />
                </button>
            )}
            {showToggle && (
                <button
                    type="button"
                    aria-label="Show options"
                    aria-expanded={open}
                    aria-controls={open ? listboxId : undefined}
                    disabled={disabled}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-mp-muted hover:text-mp-muted disabled:cursor-not-allowed"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={toggleOpen}
                >
                    <ChevronDownIcon size={18} className={open ? 'rotate-180' : undefined} />
                </button>
            )}
            {open && !disabled && (
                <ul
                    ref={listRef}
                    id={listboxId}
                    className="absolute z-50 mt-1 w-full max-h-60 overflow-y-auto rounded-lg border border-mp-input bg-mp-surface shadow-lg"
                    role="listbox"
                    onScroll={onLoadMore ? handleListScroll : undefined}
                    onMouseDown={onLoadMore ? (e) => e.preventDefault() : undefined}
                >
                    {showLeadingLoading && (
                        <li className="px-3 py-2 text-sm text-mp-muted">Loading…</li>
                    )}
                    {showEmpty && <li className="px-3 py-2 text-sm text-mp-muted">{emptyLabel}</li>}
                    {visible.map((opt, idx) => (
                        <li
                            key={getOptionValue(opt)}
                            id={optionId(idx)}
                            role="option"
                            aria-selected={selectedIndex === idx}
                            className={`px-3 py-2 cursor-pointer text-sm text-mp-foreground ${
                                selectedIndex === idx
                                    ? 'bg-mp-primary-content'
                                    : activeIndex === idx
                                      ? 'bg-mp-primary/10'
                                      : ''
                            }`}
                            onMouseDown={() => select(opt)}
                            onMouseEnter={() => setHighlighted(idx)}
                        >
                            {renderOption ? renderOption(opt) : getOptionLabel(opt)}
                        </li>
                    ))}
                    {loadingMore && (
                        <li className="px-3 py-2 text-sm text-mp-muted">Loading more…</li>
                    )}
                    {showLoadMoreError && (
                        <li
                            id={optionId(retryIndex)}
                            role="option"
                            aria-selected={retryActive}
                            className={`flex cursor-pointer items-center justify-between px-3 py-2 text-sm text-mp-error ${retryActive ? 'bg-mp-primary/10' : ''}`}
                            onMouseDown={requestMore}
                            onMouseEnter={() => setHighlighted(retryIndex)}
                        >
                            <span>{loadMoreError}</span>
                            <span className="font-medium underline">Retry</span>
                        </li>
                    )}
                </ul>
            )}
        </div>
    );
}
